import { ComboBox } from '@carbon/react';
import { useTranslation } from '@pos/i18n';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  PICKER_FIRST_PAGE,
  PICKER_MATCHES,
  useReferencePickerPage,
  useReferenceRecord,
  useReferenceSearch,
  type ReferenceOption,
} from './references';

interface ReferenceComboBoxProps {
  id: string;
  titleText: ReactNode;
  helperText?: string;
  placeholder?: string;
  /** The referenced resource, e.g. `catalog.item`. */
  resourceKey: string | undefined;
  value: string | null | undefined;
  onChange(value: string | null): void;
  disabled?: boolean;
  invalid?: boolean;
  invalidText?: string;
  /** Only these values may be chosen (a field limited by another one, `FieldDef.limitTo`). */
  allowed?: ReadonlySet<string>;
}

/**
 * A searchable picker of one record of another resource, fast with any number of records (ADR 0099). A resource of at most
 * `PICKER_FIRST_PAGE` records (the usual master data) is loaded whole and filtered as the user types. A larger one (items,
 * categories, stores by the thousand) shows its first records, is searched on the server as the user types (at most
 * `PICKER_MATCHES` matches, never counted) and says so under the field; the chosen record is fetched by id, so the picker
 * never offers only the first records or loses the name of the chosen one. The list floats over the page (`autoAlign`).
 */
export function ReferenceComboBox({
  id,
  titleText,
  helperText,
  placeholder,
  resourceKey,
  value,
  onChange,
  disabled,
  invalid,
  invalidText,
  allowed,
}: ReferenceComboBoxProps) {
  const { t } = useTranslation();
  const page = useReferencePickerPage(resourceKey);
  const complete = page.data?.complete ?? true;
  const local = complete || Boolean(allowed); // a limited field offers a few values, among the first records
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(text), 250);
    return () => window.clearTimeout(timer);
  }, [text]);
  const search = useReferenceSearch(resourceKey, query, !local);

  const first = useMemo(() => page.data?.options ?? [], [page.data]);
  const current = value == null || value === '' ? undefined : String(value);
  const known = current ? first.find((o) => o.value === current) : undefined;
  const record = useReferenceRecord(resourceKey, current, Boolean(current) && !page.isLoading && !known);
  const selected = known ?? record.data ?? null;

  const searching = !local && Boolean(query.trim());
  // The same array while nothing changed, so the list isn't rebuilt (and its highlight reset) on every render.
  const items = useMemo(() => {
    let options: ReferenceOption[] = searching ? (search.data?.options ?? []) : first;
    if (allowed) options = options.filter((o) => allowed.has(o.value));
    return selected && !options.some((o) => o.value === selected.value) ? [selected, ...options] : options;
  }, [searching, search.data, first, allowed, selected]);
  const loading = page.isLoading || (!local && search.isFetching);
  // A large resource says how much it shows, so nobody scrolls for a record that only a search finds.
  const hint = local
    ? undefined
    : searching
      ? search.data?.more
        ? t('admin.pickerMatches', { count: PICKER_MATCHES })
        : undefined
      : t('admin.pickerFirst', { count: PICKER_FIRST_PAGE });

  // Carbon's ComboBox also reports "nothing selected" when its selected item goes missing from the options for a moment
  // (loading, refetching, a new search), not only when the user clears it. That report comes while the value isn't shown;
  // a user's clear comes while it is. Only the latter may empty the value, or saving a form would silently drop a reference
  // nobody touched.
  return (
    <ComboBox<ReferenceOption>
      id={id}
      // The menu floats (Carbon floating-ui) instead of sitting in the page: in a dialog it would add to the dialog body's height
      // and toggle its scrollbar on and off as it opens, which shakes the dialog; it also flips up when there is no room below.
      autoAlign
      titleText={titleText}
      helperText={helperText ?? hint}
      disabled={disabled}
      placeholder={placeholder ?? (loading ? t('common.loading') : local ? t('admin.choose') : t('admin.typeToSearch'))}
      items={items}
      itemToString={(option) => option?.label ?? ''}
      selectedItem={selected}
      invalid={invalid}
      invalidText={invalidText}
      shouldFilterItem={({ item, inputValue }) =>
        !local || !inputValue || item.label.toLowerCase().includes(inputValue.toLowerCase())
      }
      onInputChange={(input) => setText(input ?? '')}
      onChange={({ selectedItem }) => {
        if (!selectedItem && current != null && !selected) return;
        onChange(selectedItem?.value ?? null);
      }}
    />
  );
}
